// Tipos dos componentes disponíveis
export type ComponentType = 
  | 'metric-card'
  | 'chart'
  | 'table'
  | 'relationship-table'
  | 'list'
  | 'text'
  | 'divider'
  | 'button'
  | 'kanban'
  | 'filter'
  | 'calendar'
  | 'gantt'
  | 'forms-manager'
  | 'container';

export type ChartType = 'line' | 'bar' | 'pie' | 'area';

export interface ComponentConfig {
  title?: string;
  icon?: string;
  dataSource?: DataSourceBinding;
  style?: ComponentStyle;
  [key: string]: any;
}

export interface DataSourceBinding {
  type: 'static' | 'binding' | 'expression';
  sourceId?: string;
  path?: string;
  expression?: string;
  data?: any;
}

export interface ComponentStyle {
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  padding?: number;
}

export interface PageComponent {
  id: string;
  type: ComponentType;
  position: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  config: ComponentConfig;
  _pageName?: string; // Nome da página de origem (usado para referência entre páginas)
  _pageSlug?: string; // Slug da página de origem (usado para referência entre páginas)
}

// Template de componentes disponíveis
export interface ComponentTemplate {
  id: string;
  type: ComponentType;
  name: string;
  description: string;
  icon: string;
  category: 'analytics' | 'data' | 'content' | 'layout' | 'filters';
  defaultConfig: ComponentConfig;
  defaultSize: { w: number; h: number };
}

export const COMPONENT_TEMPLATES: ComponentTemplate[] = [
  {
    id: 'metric-card',
    type: 'metric-card',
    name: 'Card de Métrica',
    description: 'Exibe um valor numérico com título',
    icon: '📊',
    category: 'analytics',
    defaultConfig: {
      title: 'Nova Métrica',
      icon: '💰',
      dataSource: {
        type: 'static',
        data: 0,
      },
    },
    defaultSize: { w: 12, h: 5 },
  },
  {
    id: 'chart',
    type: 'chart',
    name: 'Gráfico',
    description: 'Gráfico de linha, barra ou pizza',
    icon: '📈',
    category: 'analytics',
    defaultConfig: {
      title: 'Novo Gráfico',
      chartType: 'line',
      dataSource: {
        type: 'static',
        data: [],
      },
    },
    defaultSize: { w: 24, h: 12 },
  },
  {
    id: 'table',
    type: 'table',
    name: 'Tabela de Dados',
    description: 'Tabela com colunas customizáveis',
    icon: '📋',
    category: 'data',
    defaultConfig: {
      title: 'Nova Tabela',
      columns: [],
      dataSource: {
        type: 'static',
        data: [],
      },
    },
    defaultSize: { w: 48, h: 15 },
  },
  {
    id: 'relationship-table',
    type: 'relationship-table',
    name: 'Tabela de Relacionamento',
    description: 'Tabela para relacionar dois conjuntos de dados',
    icon: '🔗',
    category: 'data',
    defaultConfig: {
      title: 'Relacionamentos',
      leftTable: {
        id: '',
        name: 'Tabela A',
        data: [],
        keyField: 'id',
        displayField: 'name',
      },
      rightTable: {
        id: '',
        name: 'Tabela B',
        data: [],
        keyField: 'id',
        displayField: 'name',
      },
      leftKeyField: 'leftId',
      rightKeyField: 'rightId',
      columns: [],
      dataSource: {
        type: 'static',
        data: [],
      },
    },
    defaultSize: { w: 48, h: 15 },
  },
  {
    id: 'list',
    type: 'list',
    name: 'Lista',
    description: 'Lista de itens',
    icon: '📝',
    category: 'data',
    defaultConfig: {
      title: 'Nova Lista',
      dataSource: {
        type: 'static',
        data: [],
      },
    },
    defaultSize: { w: 16, h: 20 },
  },
  {
    id: 'text',
    type: 'text',
    name: 'Texto',
    description: 'Bloco de texto ou markdown',
    icon: '📄',
    category: 'content',
    defaultConfig: {
      content: 'Digite seu texto aqui...',
      fontSize: 14,
    },
    defaultSize: { w: 15, h: 5 },
  },
  {
    id: 'divider',
    type: 'divider',
    name: 'Divisor',
    description: 'Linha divisória',
    icon: '➖',
    category: 'layout',
    defaultConfig: {
      style: {
        borderColor: '#e5e7eb',
      },
    },
    defaultSize: { w: 48, h: 2 },
  },
  {
    id: 'container',
    type: 'container',
    name: 'Container / Seção',
    description: 'Caixa para agrupar e organizar outros componentes',
    icon: '🗂️',
    category: 'layout',
    defaultConfig: {
      title: '',
      direction: 'vertical',
      gap: 16,
      children: [],
      style: {
        padding: 16,
      },
    },
    defaultSize: { w: 48, h: 16 },
  },
  {
    id: 'button',
    type: 'button',
    name: 'Botão de Ação',
    description: 'Botão que abre formulário de cadastro',
    icon: '🔘',
    category: 'data',
    defaultConfig: {
      label: 'Novo Registro',
      sourceComponentId: undefined,
      style: {
        backgroundColor: '#3b82f6',
        textColor: '#ffffff',
      },
    },
    defaultSize: { w: 10, h: 3 },
  },
  {
    id: 'kanban',
    type: 'kanban',
    name: 'Quadro Kanban',
    description: 'Quadro com colunas e cards arrastáveis',
    icon: '📋',
    category: 'data',
    defaultConfig: {
      title: 'Kanban',
      columns: [
        {
          id: 'todo',
          title: 'A Fazer',
          icon: '📝',
          cards: [
            {
              id: '1',
              title: 'Tarefa de exemplo',
              description: 'Descrição da tarefa',
              badge: 'Alta',
              color: 'red',
            },
          ],
        },
        {
          id: 'doing',
          title: 'Em Progresso',
          icon: '⚙️',
          cards: [],
        },
        {
          id: 'done',
          title: 'Concluído',
          icon: '✅',
          cards: [],
        },
      ],
    },
    defaultSize: { w: 48, h: 22 },
  },
  {
    id: 'filter-custom',
    type: 'filter',
    name: 'Filtro Customizado',
    icon: '🔍',
    description: 'Filtro personalizado com campo e opções customizáveis',
    category: 'filters',
    defaultConfig: {
      preset: 'custom',
      field: '',
      options: [],
    },
    defaultSize: { w: 12, h: 6 },
  },
  {
    id: 'filter-date',
    type: 'filter',
    name: 'Filtro Data',
    icon: '📅',
    description: 'Filtro por data',
    category: 'filters',
    defaultConfig: {
      preset: 'monthly',
    },
    defaultSize: { w: 12, h: 6 },
  },
  {
    id: 'filter-period',
    type: 'filter',
    name: 'Filtro Período',
    icon: '📅',
    description: 'Filtro por período de datas',
    category: 'filters',
    defaultConfig: {
      preset: 'period',
    },
    defaultSize: { w: 12, h: 6 },
  },
  {
    id: 'calendar',
    type: 'calendar',
    name: 'Calendário',
    icon: '📅',
    description: 'Calendário com eventos e agenda',
    category: 'data',
    defaultConfig: {
      title: 'Calendário',
      events: [],
      viewMode: 'month',
      editable: true,
    },
    defaultSize: { w: 22, h: 12 },
  },
  {
    id: 'gantt',
    type: 'gantt',
    name: 'Cronograma',
    description: 'Gráfico de Gantt com tarefas e datas',
    icon: '📅',
    category: 'data',
    defaultConfig: {
      title: 'Cronograma',
      tasks: [],
      viewMonths: 2,
      defaultZoomMode: 'days',
      showProgress: true,
      editable: true,
    },
    defaultSize: { w: 48, h: 18 },
  },
  {
    id: 'forms-manager',
    type: 'forms-manager',
    name: 'Gerenciador de Formulários',
    icon: '🧩',
    description: 'Cria, edita e acompanha formulários do workspace',
    category: 'data',
    defaultConfig: {
      title: 'Formulários',
      forms: [],
    },
    defaultSize: { w: 48, h: 20 },
  },
];
